import { useState } from 'react';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { api } from '../api';
import { ErrorAlert, Loading } from '../components/ui';

// Preferências de alertas (seção 16): cada consultor configura as suas — canais, silêncio e intervalos.

interface Prefs {
  emailEnabled: boolean;
  immediateKinds: string[];
  digestEnabled: boolean;
  digestTime: string;
  quietStart: string;
  quietEnd: string;
  outcomeDelayMinutes: number;
  draftAfterHours: number;
  reminderOffsetsHours: number[];
  kinds: { kind: string; label: string }[];
  emailConfigured: boolean;
}

type Form = Omit<Prefs, 'kinds' | 'emailConfigured' | 'reminderOffsetsHours'> & { offsets: string };

export function AlertPreferencesForm() {
  const q = useQuery({ queryKey: ['alert-preferences'], queryFn: () => api.get<Prefs>('/me/alert-preferences') });
  if (q.isLoading || !q.data) return q.error ? <ErrorAlert error={q.error} /> : <Loading />;
  const { kinds: _k, emailConfigured: _e, reminderOffsetsHours, ...rest } = q.data;
  return <PrefsEditor prefs={q.data} initial={{ ...rest, offsets: reminderOffsetsHours.join(', ') }} />;
}

function PrefsEditor({ prefs, initial }: { prefs: Prefs; initial: Form }) {
  const qc = useQueryClient();
  const [form, setForm] = useState<Form>(initial);
  const [saved, setSaved] = useState(false);
  const m = useMutation({
    mutationFn: () => {
      const { offsets, ...rest } = form;
      const reminderOffsetsHours = offsets
        .split(/[,;\s]+/)
        .filter(Boolean)
        .map((x) => Number(x));
      if (reminderOffsetsHours.some((x) => !Number.isInteger(x) || x < 1)) throw new Error('Antecedências: use horas inteiras separadas por vírgula (ex.: 24, 2).');
      return api.put('/me/alert-preferences', { ...rest, reminderOffsetsHours });
    },
    onSuccess: () => {
      setSaved(true);
      for (const key of [['alert-preferences'], ['reminders'], ['alerts-summary']]) void qc.invalidateQueries({ queryKey: key });
    },
  });
  const set = <K extends keyof Form>(k: K, v: Form[K]) => {
    setSaved(false);
    setForm((f) => ({ ...f, [k]: v }));
  };
  const toggleKind = (k: string) => set('immediateKinds', form.immediateKinds.includes(k) ? form.immediateKinds.filter((x) => x !== k) : [...form.immediateKinds, k]);

  return (
    <section className="card form" style={{ maxWidth: 860 }}>
      <h2>Meus alertas</h2>
      <p className="small muted">Valem só para você. Alertas sempre aparecem no aplicativo (sino no topo); aqui você escolhe o que também vai por e-mail e quando.</p>
      {!prefs.emailConfigured && <div className="alert alert-warning">O envio de e-mail não está configurado (aba Integrações): por enquanto, os alertas aparecem só no aplicativo.</div>}
      <ErrorAlert error={m.error} />
      {saved && <div className="alert alert-success">Preferências salvas.</div>}

      <label className="check">
        <input type="checkbox" checked={form.emailEnabled} onChange={(e) => set('emailEnabled', e.target.checked)} /> Receber alertas por e-mail
      </label>
      <fieldset disabled={!form.emailEnabled} style={{ border: 0, padding: 0, margin: 0, display: 'grid', gap: '0.75rem' }}>
        <div>
          <h3>E-mail imediato</h3>
          <p className="small muted">Um e-mail por alerta novo (uma única vez). Os demais entram só no resumo diário.</p>
          <div className="prefs-kinds">
            {prefs.kinds.map((k) => (
              <label key={k.kind} className="check">
                <input type="checkbox" checked={form.immediateKinds.includes(k.kind)} onChange={() => toggleKind(k.kind)} /> {k.label}
              </label>
            ))}
          </div>
        </div>
        <div>
          <h3>Resumo diário das pendências</h3>
          <div className="form-row">
            <label className="check">
              <input type="checkbox" checked={form.digestEnabled} onChange={(e) => set('digestEnabled', e.target.checked)} /> Enviar resumo diário
            </label>
            <label className="field">
              <span className="small">Horário</span>
              <input type="time" value={form.digestTime} disabled={!form.digestEnabled} onChange={(e) => set('digestTime', e.target.value)} />
            </label>
          </div>
        </div>
        <div>
          <h3>Horário de silêncio</h3>
          <p className="small muted">Nesse intervalo nenhum e-mail imediato é enviado; os alertas esperam o fim do silêncio (horário de Fortaleza).</p>
          <div className="form-row">
            <label className="field">
              <span className="small">Das</span>
              <input type="time" value={form.quietStart} onChange={(e) => set('quietStart', e.target.value)} />
            </label>
            <label className="field">
              <span className="small">Até</span>
              <input type="time" value={form.quietEnd} onChange={(e) => set('quietEnd', e.target.value)} />
            </label>
          </div>
        </div>
      </fieldset>

      <h3>Intervalos</h3>
      <div className="form-row">
        <label className="field">
          <span className="small">Pendência de resultado: minutos após o término do compromisso</span>
          <input type="number" min={0} max={1440} value={form.outcomeDelayMinutes} onChange={(e) => set('outcomeDelayMinutes', Number(e.target.value))} />
        </label>
        <label className="field">
          <span className="small">Lembrar de completar rascunho após (horas)</span>
          <input type="number" min={1} max={720} value={form.draftAfterHours} onChange={(e) => set('draftAfterHours', Number(e.target.value))} />
        </label>
      </div>
      <label className="field">
        <span className="small">Lembretes ao cliente sugeridos com antecedência de (horas, separadas por vírgula)</span>
        <input value={form.offsets} onChange={(e) => set('offsets', e.target.value)} placeholder="24, 2" />
      </label>
      <p className="small muted">Lembretes ao cliente só são sugeridos na fila; o envio é sempre manual, com conferência.</p>
      <div className="actions">
        <button className="btn btn-primary" disabled={m.isPending} onClick={() => m.mutate()}>
          Salvar preferências
        </button>
      </div>
    </section>
  );
}
